import { useEffect, useState } from 'react';
import { api, type CopyAction, type CopyHistoryEntry } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { ETIQUETAS_CAMPO, IDIOMAS } from '@/components/copy/tipos';
import { ArrowLeft, ChevronRight, Languages, Lightbulb, Sparkles } from 'lucide-react';

interface Props {
  abierto: boolean;
  anuncioId: number;
  onCerrar: () => void;
  onAplicar: (fields: Record<string, string>) => void;
}

const ETIQUETAS_ACCION: Record<CopyAction, string> = {
  generate_field: 'Generación de campo',
  generate_all: 'Generación completa',
  translate_field: 'Traducción',
  generate_title: 'Títulos IA',
};

function etiquetaAccion(accion: string): string {
  return (ETIQUETAS_ACCION as Record<string, string>)[accion] ?? accion;
}

function IconoAccion({ accion }: { accion: string }) {
  const clase = 'text-sereno-dark/40 flex-shrink-0';
  if (accion === 'translate_field') return <Languages size={16} className={clase} />;
  if (accion === 'generate_title') return <Lightbulb size={16} className={clase} />;
  return <Sparkles size={16} className={clase} />;
}

function nombreIdioma(codigo: string): string {
  return IDIOMAS.find((i) => i.code === codigo)?.label ?? codigo.toUpperCase();
}

/** El historial guarda la clave del campo tal cual; puede no ser de las 14 conocidas. */
function etiquetaCampo(clave: string): string {
  return (ETIQUETAS_CAMPO as Record<string, string>)[clave] ?? clave;
}

function contarCampos(entrada: CopyHistoryEntry): number {
  return Object.keys(entrada.fields || {}).length;
}

export default function ModalHistorial({
  abierto,
  anuncioId,
  onCerrar,
  onAplicar,
}: Props) {
  const [entradas, setEntradas] = useState<CopyHistoryEntry[]>([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [detalle, setDetalle] = useState<CopyHistoryEntry | null>(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState<string | null>(null);

  useEffect(() => {
    if (!abierto) return;
    setDetalle(null);
    setErrorDetalle(null);
    setCargandoDetalle(false);
    setCargando(true);
    setError(null);

    // El modal no se desmonta al cerrarse, así que la respuesta de una apertura
    // anterior podría pisar la lista de la actual.
    let vigente = true;
    api
      .getCopyHistory(anuncioId)
      .then((res) => { if (vigente) setEntradas(res.history || []); })
      .catch((err: unknown) => {
        if (vigente) setError(err instanceof Error ? err.message : 'No se pudo cargar el historial');
      })
      .finally(() => { if (vigente) setCargando(false); });

    return () => { vigente = false; };
  }, [abierto, anuncioId]);

  async function abrirDetalle(entryId: string) {
    setCargandoDetalle(true);
    setErrorDetalle(null);
    setDetalle(null);
    try {
      const res = await api.getCopyHistoryEntry(anuncioId, entryId);
      setDetalle(res.entry ?? null);
    } catch (err) {
      setErrorDetalle(err instanceof Error ? err.message : 'No se pudo cargar la entrada');
    } finally {
      setCargandoDetalle(false);
    }
  }

  const volver = () => {
    setDetalle(null);
    setErrorDetalle(null);
  };

  const aplicar = () => {
    if (!detalle) return;
    onAplicar(detalle.fields || {});
    onCerrar();
  };

  const enDetalle = detalle !== null || cargandoDetalle || errorDetalle !== null;

  return (
    <Modal isOpen={abierto} onClose={onCerrar} title="Historial de generaciones" size="xl">
      {!enDetalle && (
        <div>
          {cargando && <LoadingSpinner />}

          {!cargando && error && <p className="text-sm text-rose-600">{error}</p>}

          {!cargando && !error && entradas.length === 0 && (
            <EmptyState
              title="Sin generaciones todavía"
              description="Aquí aparecerán los textos que genere o traduzca la IA para este anuncio."
            />
          )}

          {!cargando && !error && entradas.length > 0 && (
            <div className="space-y-2">
              {entradas.map((entrada) => (
                <button
                  key={entrada.id}
                  type="button"
                  onClick={() => { void abrirDetalle(entrada.id); }}
                  className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors flex items-center gap-3"
                >
                  <IconoAccion accion={entrada.action} />
                  <span className="flex-1 min-w-0">
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span className="text-sm font-medium text-sereno-dark">
                        {etiquetaAccion(entrada.action)}
                      </span>
                      {entrada.language && (
                        <span
                          title={nombreIdioma(entrada.language)}
                          className="badge bg-blue-100 text-blue-700"
                        >
                          {entrada.language.toUpperCase()}
                        </span>
                      )}
                      {entrada.field_key && (
                        <span className="badge bg-gray-100 text-gray-600">
                          {etiquetaCampo(entrada.field_key)}
                        </span>
                      )}
                    </span>
                    <span className="block text-xs text-sereno-dark/50 mt-1">
                      {contarCampos(entrada)}{' '}
                      {contarCampos(entrada) === 1 ? 'campo' : 'campos'}
                      {' · '}
                      {formatDateTime(entrada.created_at)}
                    </span>
                  </span>
                  <ChevronRight size={16} className="text-sereno-dark/30 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}

          <div className="flex justify-end pt-4">
            <button type="button" className="btn-ghost" onClick={onCerrar}>
              Cerrar
            </button>
          </div>
        </div>
      )}

      {enDetalle && (
        <div className="space-y-4">
          <button
            type="button"
            className="btn-ghost flex items-center gap-2 text-sm -ml-4"
            onClick={volver}
          >
            <ArrowLeft size={16} />
            Volver al historial
          </button>

          {cargandoDetalle && <LoadingSpinner />}

          {!cargandoDetalle && errorDetalle && (
            <p className="text-sm text-rose-600">{errorDetalle}</p>
          )}

          {!cargandoDetalle && detalle && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="badge bg-purple-100 text-purple-700">
                  {etiquetaAccion(detalle.action)}
                </span>
                {detalle.language && (
                  <span
                    title={nombreIdioma(detalle.language)}
                    className="badge bg-blue-100 text-blue-700"
                  >
                    {detalle.language.toUpperCase()}
                  </span>
                )}
                {detalle.field_key && (
                  <span className="badge bg-gray-100 text-gray-600">
                    {etiquetaCampo(detalle.field_key)}
                  </span>
                )}
                <span className="text-xs text-sereno-dark/50">
                  {formatDateTime(detalle.created_at)}
                </span>
              </div>

              {contarCampos(detalle) === 0 ? (
                <p className="text-sm text-sereno-dark/50">
                  Esta entrada no guardó ningún texto.
                </p>
              ) : (
                <div className="space-y-4">
                  {Object.entries(detalle.fields).map(([clave, texto]) => (
                    <div key={clave}>
                      <p className="label">{etiquetaCampo(clave)}</p>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-sereno-dark whitespace-pre-wrap break-words">
                        {texto}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
                {contarCampos(detalle) > 0 && (
                  <p className="text-xs text-sereno-dark/50 flex-1 min-w-[200px]">
                    Los textos se escriben en el editor <strong>sin guardarse</strong>: hay
                    que pulsar Guardar para que lleguen a Hostify.
                  </p>
                )}
                <button type="button" className="btn-ghost" onClick={onCerrar}>
                  Cerrar
                </button>
                {contarCampos(detalle) > 0 && (
                  <button type="button" className="btn-primary" onClick={aplicar}>
                    Aplicar estos textos
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
